<template>
  <div class="hot-deals-page">
    <h1>Hot Cruise Deals</h1>
    <p class="subtitle">More price drops and value picks beyond the Home highlights</p>

    <div class="deal-page-trust-banner">
      <span class="trust-badge">DATA BASIS</span>
      <p class="trust-text">
        Fares are tracked daily from cruise line booking sources. &quot;Save&quot; amounts compare current fares against our 30-day recorded peak for each cabin and sail date, not cruise line list prices.
      </p>
    </div>

    <div class="deal-page-trust-banner home-diff-banner" role="note">
      <span class="trust-badge">VS HOME</span>
      <p class="trust-text">
        Hot Deals skips sailings already on Home's Biggest Price Drops and Best Value. If there are no other price drops, we show Home's here instead of an empty section. Savings always show in dollars and percent.
      </p>
    </div>

    <div class="deal-types-explainer">
      <deal-explainer-popover
        title="How Deal Types Work"
        :definitions="dealTypeDefinitions"
        aria-label="How deal types are calculated"
      >
        <template #trigger>
          <span class="explainer-trigger-label"><span class="explainer-trigger-icon" aria-hidden="true">&#8505;</span> How Deal Types Work</span>
        </template>
      </deal-explainer-popover>
    </div>

    <DealFilters 
      ref="dealFilters"
      :filters="filters" 
      @filter-change="handleFilterChange"
      @options-loaded="filterLabelSources = $event"
    />
    
    <div class="controls-bar">
      <div class="control-group">
        <label>
          <input type="checkbox" v-model="includePriceDrops" @change="fetchHotDeals" />
          Price Drops
        </label>
      </div>
      
      <div class="control-group">
        <label>
          <input type="checkbox" v-model="includeBestValue" @change="fetchHotDeals" />
          Best Value
        </label>
      </div>
      
      <div class="control-group">
        <label>
          <input type="checkbox" v-model="includeNewlyListed" @change="fetchHotDeals" />
          Newly Listed
        </label>
      </div>
    </div>
    
    <HotDealsList 
      :price-drop-deals="priceDropDeals"
      :price-drops-enabled="includePriceDrops"
      :best-value-deals="bestValueDeals"
      :newly-listed="newlyListed"
      :loading="loading"
      :first-load="firstLoad"
      :error="error"
      :filtered-empty="filteredEmpty"
      :recovery-options="relaxOptions"
      :differentiated-from-home="differentiatedFromHome"
      :price-drops-overlap-home="priceDropsOverlapHome"
      :affiliate-enabled="affiliateEnabled"
      @refresh="fetchHotDeals"
      @clear-filters="clearAllFilters"
      @relax-filter="relaxFilter"
    />
  </div>
</template>

<script>
import HotDealsList from '@/components/HotDealsList.vue';
import DealFilters from '@/components/DealFilters.vue';
import DealExplainerPopover from '@/components/DealExplainerPopover.vue';
import api from '@/services/api';
import { affiliateState } from '@/utils/affiliateFlag';
import { dealRelaxOptions } from '@/utils/emptyRecovery';
import { realPriceDropDeals } from '@/utils/priceDropBadge';
import { hotDealsRequestParams } from '@/utils/hotPriceDrops';
import { labelForDestinationId } from '@/utils/canonicalDestinations';
import { labelForDeparturePort } from '@/utils/canonicalDeparturePorts';

export default {
  name: 'HotDeals',
  components: {
    HotDealsList,
    DealFilters,
    DealExplainerPopover
  },
  data() {
    return {
      priceDropDeals: [],
      bestValueDeals: [],
      newlyListed: [],
      loading: true,
      // Deals CLS: false until the first fetch has settled (HotDealsList .loading-indicator--first).
      hasLoaded: false,
      error: null,
      includePriceDrops: true,
      includeBestValue: true,
      includeNewlyListed: false,
      // CP-95: API excluded Home shelf membership on this fetch.
      differentiatedFromHome: true,
      // CP-237: Price Drops are the Home highlights (too few to skip).
      priceDropsOverlapHome: false,
      filters: {},
      // CP-127: DealFilters' option lists, for recovery action labels.
      filterLabelSources: { destinations: [], departurePorts: [] },
      debounceTimer: null
    };
  },
  computed: {
    /** Deals CLS: initial load only: loading and no fetch has settled yet. */
    firstLoad() {
      return this.loading && !this.hasLoaded && !this.error;
    },
    // CP-142: Hot Deals CTAs follow the single runtime flag from /api/config.
    affiliateEnabled() {
      return affiliateState.enabled;
    },
    /** CP-127: one "Remove ..." action per applied filter, most restrictive first. */
    relaxOptions() {
      const src = this.filterLabelSources || {};
      return dealRelaxOptions(this.filters, {
        destination: (id) => labelForDestinationId(src.destinations || [], id),
        departure_port: (key) => labelForDeparturePort(src.departurePorts || [], key)
      });
    },
    /**
     * CP-127: filters are active, at least one deal type is on, and every
     * enabled section came back empty.
     */
    filteredEmpty() {
      if (this.loading || this.error || this.relaxOptions.length === 0) return false;
      if (!this.includePriceDrops && !this.includeBestValue && !this.includeNewlyListed) return false;
      const drops = this.includePriceDrops ? realPriceDropDeals(this.priceDropDeals).length : 0;
      return drops === 0 && this.bestValueDeals.length === 0 && this.newlyListed.length === 0;
    },
    dealTypeDefinitions() {
      return [
        {
          label: 'Price Drop',
          body: 'We monitor daily fares directly from cruise line listings. When a cabin on a specific sail date drops below its 30-day recorded peak, we flag it as a Price Drop with the dollar and percentage savings.'
        },
        {
          label: 'Best Value',
          body: 'Calculated by dividing the lowest available cabin fare by voyage length (fare ÷ nights). Ranks cruises that deliver the lowest per-night cost.'
        },
        {
          label: 'Newly Listed',
          body: 'Cruises and itineraries first cataloged by Cruise Signals in the last 7 days.'
        }
      ];
    }
  },
  mounted() {
    this.fetchHotDeals();
  },
  beforeUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  methods: {
    /** CP-127: recovery actions run through DealFilters (auto-apply, no reload). */
    clearAllFilters() {
      const panel = this.$refs.dealFilters;
      if (panel && typeof panel.resetFilters === 'function') panel.resetFilters();
      this.flushFilterFetch();
    },
    relaxFilter(option) {
      const panel = this.$refs.dealFilters;
      if (!option || !option.patch) return;
      if (panel && typeof panel.clearFilter === 'function') panel.clearFilter(option.patch);
      this.flushFilterFetch();
    },
    /** A recovery tap is one deliberate action: fetch now, skip the typing debounce. */
    flushFilterFetch() {
      if (!this.debounceTimer) return;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.fetchHotDeals();
    },
    handleFilterChange(newFilters) {
      this.filters = newFilters;
      this.debounceFilterChange();
    },
    
    debounceFilterChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      this.debounceTimer = setTimeout(() => {
        this.fetchHotDeals();
      }, 300);
    },
    
    async fetchHotDeals() {
      this.loading = true;
      this.error = null;
      
      try {
        // CP-237: no lookback_hours; the endpoint has no time window.
        const params = hotDealsRequestParams({
          includePriceDrops: this.includePriceDrops,
          includeBestValue: this.includeBestValue,
          includeNewlyListed: this.includeNewlyListed
        }, this.filters);
        
        const response = await api.getHotDeals(params);
        this.priceDropDeals = response.data.price_drops || [];
        this.bestValueDeals = response.data.best_value || [];
        this.newlyListed = response.data.newly_listed || [];
        this.differentiatedFromHome = response.data.differentiated_from_home !== false;
        this.priceDropsOverlapHome = response.data.price_drops_overlap_home === true;
      } catch (error) {
        console.error('Error fetching hot deals:', error);
        this.error = 'Unable to load hot deals. Please try again later.';
      } finally {
        this.loading = false;
        this.hasLoaded = true;
      }
    }
  }
};
</script>

<style scoped>
.hot-deals-page {
  margin: 0;
  padding: 0;
}

h1 {
  color: var(--color-text);
  font-family: var(--font-heading);
  text-align: center;
  margin-bottom: var(--space-2);
}

.subtitle {
  text-align: center;
  color: var(--color-text-muted);
  margin-bottom: var(--space-5);
}

.home-diff-banner {
  margin-top: var(--space-3);
}

.deal-page-trust-banner {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--cs-sand);
  border: 1px solid var(--cs-card-border);
  border-left: 3px solid var(--cs-signal-teal);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  margin: var(--space-3) 0 var(--space-4) 0;
}

.trust-badge {
  background: var(--cs-signal-mist);
  color: var(--cs-signal-teal);
  font-family: var(--font-heading);
  font-weight: bold;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.05em;
  padding: 2px 6px;
  border-radius: var(--radius-pill);
  flex-shrink: 0;
}

.trust-text {
  margin: 0;
  font-size: var(--text-small);
  color: var(--cs-ink);
  line-height: var(--line-height-body);
}

.deal-types-explainer {
  display: flex;
  justify-content: flex-end;
  margin: var(--space-2) 0 var(--space-4) 0;
}

.explainer-trigger-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-small);
  font-weight: 600;
  color: var(--cs-signal-teal);
}

.explainer-trigger-icon {
  font-size: 1rem;
}

.controls-bar {
  display: flex;
  justify-content: center;
  gap: var(--space-5);
  margin-bottom: var(--space-5);
  padding: var(--space-4);
  background-color: var(--color-surface);
  border-radius: var(--radius-md);
  flex-wrap: wrap;
  align-items: center;
  box-shadow: var(--shadow-card);
}

.control-group {
  display: flex;
  flex-direction: column;
  min-width: 120px;
}

.control-group label {
  margin-bottom: var(--space-2);
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: var(--text-small);
  display: flex;
  align-items: center;
  cursor: pointer;
}

.control-group input[type="checkbox"] {
  margin-right: var(--space-2);
  accent-color: var(--color-primary);
}

@media (max-width: 640px) {
  .controls-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .control-group {
    width: 100%;
    min-width: 0;
  }
}
</style>
